import {
  useCallback, useMemo, useState
} from "react";
import { Route, Switch as RouteSwitch } from "react-router-dom";

import clsx from "clsx";
import { Switch } from "@headlessui/react";
import { Panel } from "@k8slens/lds";

import { useAnalytics } from "src/hooks/useAnalytics";
import { PlanCode } from "src/services/plans";

import Notification from "src/components/Notification/Notification";
import { License } from "src/components/pages/Home/license";
import TrackedLink from "src/components/TrackedLink/TrackedLink";
import TrackedAnchor from "src/components/TrackedAnchor/TrackedAnchor";

import { TrialState } from "./common";
import ChoosePro from "./ChoosePro";
import ChoosePersonal from "./ChoosePersonal";
import ChoosePlanAside from "./ChoosePlanAside";

import styles from "./ChoosePlan.module.css";

export interface ChoosePlanProps {
  // If license activation failed, we must display the error message
  licenseError?: string | null;
  loading?: boolean;
  trialState: TrialState | null;
  license: License | null;
  onChoosePlan(planCode: PlanCode): Promise<boolean>;
}

const ChoosePlan: React.FC<ChoosePlanProps> = ({
  licenseError, license, loading, trialState, onChoosePlan
}) => {
  const { track } = useAnalytics();

  const [error, setError] = useState<string | undefined>(undefined);

  const handleChoosePlan = useCallback((plan: PlanCode) => {
    setError(undefined);

    return onChoosePlan(plan)
      .catch(() => {
        setError("Something went wrong. Please try again later.");

        return false;
      });
  }, [onChoosePlan]);

  const [annual, setAnnual] = useState<boolean>(false);
  const handleAnnualChange = useCallback((next: boolean) => {
    setAnnual(next);
    annual !== next && track("Toggle Billing Cycle", { value: next ? "Annual" : "Monthly" });
  }, [annual, track]);

  const handleChooseMonthly = () => annual && handleAnnualChange(false);
  const handleChooseAnnual = () => !annual && handleAnnualChange(true);

  return (
    <div className={styles.choosePlan}>
      <ChoosePlanAside highlightManagerEmail />
      <Panel
        header={(
          <>
            <h2>Choose Your Lens Subscription</h2>
            <Switch.Group>
              <div className={styles.billingSwitch}>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label
                  aria-hidden
                  htmlFor="annual-switch"
                  className={clsx({ [styles.passive]: !annual })}
                  onClick={handleChooseMonthly}
                >
                  Monthly billing
                </label>
                <Switch
                  id="annual-switch"
                  checked={annual}
                  onChange={handleAnnualChange}
                  aria-label="Choose annual instead of monthly billing plan"
                >
                  <span />
                </Switch>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label
                  aria-hidden
                  htmlFor="annual-switch"
                  className={clsx({ [styles.passive]: annual })}
                  onClick={handleChooseAnnual}
                >
                  Annual billing
                </label>
              </div>
            </Switch.Group>
          </>
        )}
        contentComponent="div"
        contentProps={useMemo(() => ({
          className: styles.licenseSelection
        }), [])}
        footer={(
          <RouteSwitch>
            <Route path="/subscribe">
              <TrackedLink to="/subscribe-personal" className={clsx("primary", styles.switchPlan)}>
                Lens Subscriptions for Students, Hobbyists and Startups
              </TrackedLink>
            </Route>
            <Route path="/subscribe-personal">
              <TrackedLink to="/subscribe" className={clsx("primary", styles.switchPlan)}>
                Lens Subscriptions for Professionals and those who don’t meet the eligibility
              </TrackedLink>
            </Route>
          </RouteSwitch>
        )}
        footerProps={useMemo(() => ({
          className: styles.footer
        }), [])}
      >
        <>
          { (licenseError || error) && <Notification type="flash" level="error" message={licenseError || error} className={styles.errorNotification} /> }
          <RouteSwitch>
            <Route path="/subscribe">
              { trialState === "used" && (
              <div className={styles.trialEndNotification}>
                {/* eslint-disable-next-line max-len */}
                Need help convincing your boss? Need more time to onboard your teams? We’ll figure out something that works for you and your team.
                {" "}
                <TrackedAnchor href="https://public.refiner.io/s/eolx2e/9669d4a0-7a07-11ed-b065-2f290bc11892" target="_blank">Let us help!</TrackedAnchor>
              </div>
              )}
              <article className={styles.plan}>
                <ChoosePro loading={loading} annual={annual} trialState={trialState} onChoosePlan={handleChoosePlan} />
              </article>
            </Route>
            <Route path="/subscribe-personal">
              <article className={styles.plan}>
                <ChoosePersonal license={license} loading={loading} onChoosePlan={handleChoosePlan} />
              </article>
            </Route>
          </RouteSwitch>
        </>
      </Panel>
    </div>
  );
};

export default ChoosePlan;
